<template>
  <v-row>
    <v-col>
      <v-row>
        <v-col cols="12">
          <base-card>
            <v-card-title>
              <div class="d-flex justify-space-between align-center flex-wrap w-100">
                <div class="d-flex align-center px-3 mb-4 mb-md-0" style="width: 100%;">
                  <img src="/mstile-70x70.png" height="36" alt="logo" />
                  <h5 class="ma-0 ml-4">Accounting · Per Campaign</h5>
                  <div class="flex-grow-1" aria-hidden="true"></div>
                  <v-btn
                    class="ml-4 mt-4 mt-md-0 mr-10"
                    dark
                    color="primary"
                    :loading="loading"
                    @click="loadReport"
                  </v-btn> 
                </div>
                <br/>
                <br/>
                <div class="filters-wrap d-flex flex-wrap align-center">
                  <v-autocomplete
                    dense
                    outlined
                    hide-details
                    :items="brandOptions"
                    item-text="text"
                    item-value="value"
                    label="Brand"
                    class="ma-0"
                    v-model="filters.brandId"
                    clearable
                    @change="handleFilterChange"
                  />
                  <v-autocomplete
                    dense
                    outlined
                    hide-details
                    :items="brandLeadOptions"
                    item-text="text"
                    item-value="value"
                    label="Brand Representative"
                    class="ma-0"
                    v-model="filters.brandLeadId"
                    clearable
                    @change="handleFilterChange"
                  />
                  <v-autocomplete
                    dense
                    outlined
                    hide-details
                    :items="networkOptions"
                    item-text="text"
                    item-value="value"
                    label="Network"
                    class="ma-0"
                    v-model="filters.networkId"
                    clearable
                    @change="handleFilterChange"
                  />
                  <div class="date-field">
                    <v-menu
                      v-model="dateMenus.from"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="filters.startDate"
                          label="From"
                          dense
                          outlined
                          hide-details
                          readonly
                          class="ma-0"
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker
                        v-model="filters.startDate"
                        :max="filters.endDate || datepickerMax"
                        scrollable
                        @input="() => { dateMenus.from = false; ensureValidRange(); }"
                        @change="handleFilterChange"
                      />
                    </v-menu>
                  </div>
                  <div class="date-field">
                    <v-menu
                      v-model="dateMenus.to"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="filters.endDate"
                          label="To"
                          dense
                          outlined
                          hide-details
                          readonly
                          class="ma-0"
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker
                        v-model="filters.endDate"
                        :min="filters.startDate"
                        :max="datepickerMax"
                        scrollable
                        @input="() => { dateMenus.to = false; ensureValidRange(); }"
                        @change="handleFilterChange"
                      />
                    </v-menu>
                  </div>                                   
                </div>
                <br/><br/>
              </div>
            </v-card-title>
            <v-progress-linear
              :active="loading"
              :indeterminate="loading"
              absolute
              bottom
              color="deep-purple accent-4"
            />
          </base-card>
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <v-col cols="12">
          <div class="metrics-grid">
            <div class="metrics-griditem">
              <card-dashboard
                title="Total Campaigns"
                :primaryvalue="summary.totalCampaigns"
              />
            </div>
            <div class="metrics-griditem">
              <card-dashboard
                title="Monetized Campaigns"
                :primaryvalue="summary.monetizedCampaigns"
              />
            </div>
            <div class="metrics-griditem">
              <card-dashboard
                title="Non-Monetized Campaigns"
                :primaryvalue="summary.nonMonetizedCampaigns"
              />
            </div>
            <div class="metrics-griditem">
              <card-dashboard
                title="Total Cost"
                :primaryvalue="summary.totalCost"
                unit="€"
                valueformatting="currency"
              />
            </div>
          </div>
        </v-col>
      </v-row>

      <v-row class="mt-4 mb-8">
        <v-col cols="12">
          <base-card>
            <v-card-title class="section-title">Campaigns</v-card-title>
            <v-divider />
            <v-card-text>
              <v-simple-table v-if="hasCampaignRows">
                <thead>
                  <tr>
                    <th>Campaign Name</th>
                    <th>Owner</th>
                    <th class="text-left">Monetization</th>
                    <th class="text-right">Cost</th>
                    <th class="text-right">Report</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in campaigns" :key="row.id">
                    <td>{{ row.name }}</td>
                    <td>{{ row.owner || '-' }}</td>
                    <td>
                      <div class="monetization-cell">
                        <v-icon :color="row.isMonetized ? 'success' : 'primary'">
                          {{ row.isMonetized ? 'mdi-check-circle' : 'mdi-cancel' }}
                        </v-icon>
                        <span class="ml-2">{{ row.monetization }}</span>
                      </div>
                    </td>
                    <td class="text-right">{{ formatCurrency(row.cost) }}</td>
                    <td class="text-right">
                      <v-btn
                        dark
                        icon
                        :to="'/mng/reports/overview/' + row.id"
                      >
                        <v-icon>mdi-chart-bar</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-else class="empty-state">No campaigns found for the selected filters.</div>
            </v-card-text>
          </base-card>
        </v-col>
      </v-row>

      <v-snackbar v-model="snackbar.show" :timeout="4000" color="red" bottom>
        {{ snackbar.message }}
      </v-snackbar>
    </v-col>
  </v-row>
</template>

<script>
import moment from "moment";
import { api, srvApi, PATH } from "src/api/index";
import CardDashboard from "@/components/campaigns/CardDashboard";
import { formatCurrencyEUR, formatInteger } from "@/core/number-utils";

export default {
  name: "AccountingPerCampaign",
  components: {
    CardDashboard,
  },
  data() {
    const today = moment().format("YYYY-MM-DD");
    return {
      loading: false,
      filters: {
        brandId: null,
        brandLeadId: null,
        networkId: null,
        startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
        endDate: today,
      },
      datepickerMax: today,
      dateMenus: {
        from: false,
        to: false,
      },
      summary: {
        totalCampaigns: 0,
        monetizedCampaigns: 0,
        nonMonetizedCampaigns: 0,
        totalCost: 0,
      },
      campaigns: [],
      brandOptions: [],
      brandLeadOptions: [],
      networkOptions: [],
      snackbar: {
        show: false,
        message: "",
      },
    };
  },
  computed: {
    hasCampaignRows() {
      return this.campaigns && this.campaigns.length > 0;
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    async initialize() {
      await this.loadFilterOptions();
      await this.loadReport();
    },
    async loadFilterOptions() {
      try {
        const { data } = await api.adpoint.getInitData();
        const brandItems = Array.isArray(data?.brandOptions) ? data.brandOptions : [];
        const brandLeadItems = Array.isArray(data?.brandLeadOptions) ? data.brandLeadOptions : [];
        const networkItems = Array.isArray(data?.networkOptions) ? data.networkOptions : [];

        this.brandOptions = [
          { value: null, text: "All Brands" },
          ...brandItems,
        ];
        this.brandLeadOptions = [
          { value: null, text: "All Brand Representatives" },
          ...brandLeadItems,
        ];
        this.networkOptions = [
          { value: null, text: "All Networks" },
          ...networkItems,
        ];
      } catch (error) {
        this.showError("Unable to load filter options. Please refresh and try again.");
      }
    },
    ensureValidRange() {
      if (this.filters.startDate && this.filters.endDate && this.filters.startDate > this.filters.endDate) {
        this.filters.endDate = this.filters.startDate;
      }
    },
    async loadReport() {
      this.ensureValidRange();
      this.loading = true;
      try {
        const payload = {
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          brandId: this.filters.brandId,
          brandLeadId: this.filters.brandLeadId,
          networkId: this.filters.networkId,
        };
        const { data } = await srvApi.post(PATH.REPORTING_ACCOUNTING_PER_CAMPAIGN, payload);
        this.summary = {
          totalCampaigns: data?.summary?.totalCampaigns || 0,
          monetizedCampaigns: data?.summary?.monetizedCampaigns || 0,
          nonMonetizedCampaigns: data?.summary?.nonMonetizedCampaigns || 0,
          totalCost: data?.summary?.totalCost || 0,
        };
        this.campaigns = data?.campaigns || [];
        if (data?.filters) {
          this.filters.startDate = data.filters.startDate;
          this.filters.endDate = data.filters.endDate;
        }
      } catch (error) {
        this.showError("Unable to load campaign insights. Please try again.");
      } finally {
        this.loading = false;
      }
    },
    handleFilterChange() {
      this.loadReport();
    },
    formatCurrency(value) {
      return formatCurrencyEUR(value, 2);
    },
    showError(message) {
      this.snackbar = {
        show: true,
        message,
      };
    },
  },
};
</script>

<style scoped lang="scss">
.w-100 {
  width: 100%;
}

.filters-wrap {
  gap: 12px;
}

.date-field {
  min-width: 180px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.section-title {
  font-weight: 600;
}

.empty-state {
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
}

.monetization-cell {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}
</style>
